import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Breadcrumb, Button, ContentCard, Icon, SectionHeader } from "@zorni/components-lib";
import PortalPreloader from "../../components/preloader/PortalPreloader";
import BookingSteps from "../../components/booking/bookingSteps/BookingSteps";
import TravelerForm from "../../components/booking/travelerForm/TravelerForm";
import BookingRecap from "../../components/booking/bookingRecap/BookingRecap";
import { useDetailsTrip } from "../../components/tripDetails/detailsTrip/hooks/useDetailstrip";
import { usePerson } from "../../hooks/user/usePerson";
import styles from "./Preview.module.css";
import { useBookingTracking } from "../../../tracking";
import { currentLocale } from "../../../common/i18n/locale";

const toPositiveInt = (value, fallback = 0) => {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
};

const buildTravelers = ({ nbAdults, nbChilds, nbBabies }) => {
  const travelers = [];

  for (let i = 0; i < nbAdults; i += 1) {
    travelers.push({
      firstName: "",
      lastName: "",
      birthDate: "",
      ageCategory: "ADULT",
      isMainPerson: i === 0,
    });
  }

  for (let i = 0; i < nbChilds; i += 1) {
    travelers.push({
      firstName: "",
      lastName: "",
      birthDate: "",
      ageCategory: "CHILD",
      isMainPerson: false,
    });
  }

  for (let i = 0; i < nbBabies; i += 1) {
    travelers.push({
      firstName: "",
      lastName: "",
      birthDate: "",
      ageCategory: "BABY",
      isMainPerson: false,
    });
  }

  return travelers;
};

const getTravelerTypeLabel = (ageCategory) => {
  if (ageCategory === "CHILD") return "Enfant";
  if (ageCategory === "BABY") return "Bébé";
  return "Adulte";
};

const calculateAgeAtDate = (birthDate, referenceDate) => {
  if (!birthDate || !referenceDate) return null;

  const birth = new Date(birthDate);
  const ref = new Date(referenceDate);

  if (Number.isNaN(birth.getTime()) || Number.isNaN(ref.getTime())) {
    return null;
  }

  let age = ref.getFullYear() - birth.getFullYear();
  const monthDiff = ref.getMonth() - birth.getMonth();

  if (
    monthDiff < 0 ||
    (monthDiff === 0 && ref.getDate() < birth.getDate())
  ) {
    age -= 1;
  }

  return age;
};

export function Preview() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { tripPublicationId, tripDate } = useParams();
  const { personInfo } = usePerson();

  const queryParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const counts = useMemo(() => {
    const nbAdults = Math.max(1, toPositiveInt(queryParams.get("nbAdults"), 1));
    const nbChilds = toPositiveInt(queryParams.get("nbChilds"), 0);
    const nbBabies = toPositiveInt(queryParams.get("nbBabies"), 0);

    return {
      nbAdults,
      nbChilds,
      nbBabies,
    };
  }, [queryParams]);

  const [travelers, setTravelers] = useState(() => buildTravelers(counts));
  const [showValidation, setShowValidation] = useState(false);
  const storageKey = useMemo(
    () => `preview-booking:${tripPublicationId}:${tripDate}`,
    [tripPublicationId, tripDate]
  );

  useEffect(() => {
    setTravelers(buildTravelers(counts));
  }, [counts]);

  useEffect(() => {
    try {
      sessionStorage.setItem(
        storageKey,
        JSON.stringify({
          travelers,
          counts,
        })
      );
    } catch (e) {
      console.warn("Impossible de sauvegarder les voyageurs:", e);
    }
  }, [storageKey, travelers, counts]);

  const { loading, error, tripFlight } = useDetailsTrip(tripPublicationId);
  const establishmentId = tripFlight?.tripSummary?.establishmentId;

  useBookingTracking({
    publicationId: tripPublicationId,
    establishmentId,
    enabled: !loading && !error,
  });

  const selectedTripDate = useMemo(() => {
    const list = tripFlight?.content?.flatMap((item) => item?.tripDateSummary ?? []) ?? [];
    return list.find((item) => String(item?.id) === String(tripDate)) ?? null;
  }, [tripFlight, tripDate]);

  const ageLimits = useMemo(() => {
    const firstFlightSummary = tripFlight?.content?.[0];
    const babiesMaxAge = Number.isFinite(firstFlightSummary?.babiesMaxAge)
      ? Number(firstFlightSummary.babiesMaxAge)
      : 1;
    const childsMaxAge = Number.isFinite(firstFlightSummary?.childsMaxAge)
      ? Number(firstFlightSummary.childsMaxAge)
      : 11;

    return {
      babiesMaxAge,
      childsMaxAge,
      adultsMinAge: childsMaxAge + 1,
      childsMinAge: babiesMaxAge + 1,
    };
  }, [tripFlight]);

  const tripSummary = tripFlight?.tripSummary;

  const mainPicture = useMemo(() => {
    const pictures = tripSummary?.pictures ?? [];
    return pictures.find((p) => p?.isMain)?.link || pictures[0]?.link || "";
  }, [tripSummary]);

  /* « Marrakech,Maroc » tel quel : on aère la liste des destinations. */
  const destinationLabel = (tripFlight?.destination ?? "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean)
    .join(" · ");

  const travelerStats = useMemo(() => {
    return travelers.reduce(
      (acc, traveler) => {
        if (traveler?.ageCategory === "BABY") acc.nbBabies += 1;
        else if (traveler?.ageCategory === "CHILD") acc.nbChilds += 1;
        else acc.nbAdults += 1;

        return acc;
      },
      { nbAdults: 0, nbChilds: 0, nbBabies: 0 }
    );
  }, [travelers]);

  const handleTravelerChange = (index, field, value) => {
    setTravelers((prev) => {
      const next = [...prev];
      next[index] = {
        ...next[index],
        [field]: value,
      };
      return next;
    });
  };

  /* Coordonnées du compte connecté, proposées en un clic pour le voyageur
     principal (c'est lui qui réserve). */
  const profileTraveler = useMemo(() => {
    const firstName = String(personInfo?.firstName ?? "").trim();
    const lastName = String(personInfo?.lastName ?? "").trim();

    if (!firstName && !lastName) return null;

    return {
      firstName,
      lastName,
      birthDate: String(personInfo?.dateOfBirth ?? "").slice(0, 10),
    };
  }, [personInfo]);

  const handleUseProfile = (index) => {
    if (!profileTraveler) return;

    setTravelers((prev) => {
      const next = [...prev];
      next[index] = {
        ...next[index],
        firstName: profileTraveler.firstName,
        lastName: profileTraveler.lastName,
        birthDate: profileTraveler.birthDate || next[index]?.birthDate,
      };
      return next;
    });
  };

  const handleReclassifyTraveler = (index, ageCategory) => {
    setTravelers((prev) => {
      const next = [...prev];
      const target = next[index];

      if (!target || !ageCategory) return prev;

      next[index] = {
        ...target,
        ageCategory,
        isMainPerson: ageCategory === "ADULT" ? target.isMainPerson ?? false : false,
      };

      return next;
    });
  };

  const missingTravelers = useMemo(() => {
    return travelers
      .map((traveler, index) => {
        const missingFields = [];
        if (!traveler.firstName?.trim()) missingFields.push("prénom");
        if (!traveler.lastName?.trim()) missingFields.push("nom");
        if (!traveler.birthDate) missingFields.push("date de naissance");

        if (missingFields.length === 0) return null;

        return {
          index,
          label: `Voyageur ${index + 1} (${getTravelerTypeLabel(
            traveler.ageCategory
          )})`,
          missingFields,
        };
      })
      .filter(Boolean);
  }, [travelers]);

  const invalidAgeTravelers = useMemo(() => {
    const departureDate = selectedTripDate?.startDate;
    if (!departureDate) return [];

    return travelers
      .map((traveler, index) => {
        if (!traveler?.birthDate || !traveler?.ageCategory) return null;

        const ageAtDeparture = calculateAgeAtDate(
          traveler.birthDate,
          departureDate
        );

        if (ageAtDeparture === null) return null;

        const isInvalid =
          (traveler.ageCategory === "BABY" &&
            ageAtDeparture > ageLimits.babiesMaxAge) ||
          (traveler.ageCategory === "CHILD" &&
            (ageAtDeparture <= ageLimits.babiesMaxAge ||
              ageAtDeparture > ageLimits.childsMaxAge)) ||
          (traveler.ageCategory === "ADULT" &&
            ageAtDeparture <= ageLimits.babiesMaxAge);

        if (!isInvalid) return null;

        const travelerLabel = `Voyageur ${index + 1} (${getTravelerTypeLabel(
          traveler.ageCategory
        )})`;

        let expectedLabel = "";
        let suggestedAgeCategory = null;
        let suggestedActionLabel = "";

        if (
          traveler.ageCategory === "BABY" &&
          ageAtDeparture > ageLimits.babiesMaxAge
        ) {
          if (ageAtDeparture <= ageLimits.childsMaxAge) {
            expectedLabel = "etre passe en enfant";
            suggestedAgeCategory = "CHILD";
            suggestedActionLabel = "Passer en enfant";
          } else {
            expectedLabel = "etre passe en adulte";
            suggestedAgeCategory = "ADULT";
            suggestedActionLabel = "Passer en adulte";
          }
        } else if (
          traveler.ageCategory === "CHILD" &&
          ageAtDeparture <= ageLimits.babiesMaxAge
        ) {
          expectedLabel = `${ageLimits.babiesMaxAge} an(s) maximum`;
        } else if (
          traveler.ageCategory === "CHILD" &&
          ageAtDeparture > ageLimits.childsMaxAge
        ) {
          expectedLabel = "etre passe en adulte";
          suggestedAgeCategory = "ADULT";
          suggestedActionLabel = "Passer en adulte";
        } else if (traveler.ageCategory === "ADULT") {
          expectedLabel = `${ageLimits.babiesMaxAge} an(s) maximum`;
        } else {
          expectedLabel = `entre ${ageLimits.childsMinAge} et ${ageLimits.childsMaxAge} ans`;
        }

        return {
          index,
          label: travelerLabel,
          ageAtDeparture,
          suggestedAgeCategory,
          suggestedActionLabel,
          message: `${travelerLabel} aura ${ageAtDeparture} an(s) au depart. Cette categorie doit ${expectedLabel}.`,
        };
      })
      .filter(Boolean);
  }, [travelers, selectedTripDate?.startDate, ageLimits]);

  useEffect(() => {
    if (missingTravelers.length === 0 && invalidAgeTravelers.length === 0) {
      setShowValidation(false);
    }
  }, [missingTravelers.length, invalidAgeTravelers.length]);

  /* Suppléments choisis sur la page détail : on les retrouve en session pour
     les rappeler ici, avec leur libellé et leur photo. */
  const chosenExtras = useMemo(() => {
    let acquisitions = {};
    try {
      const saved = sessionStorage.getItem(`acquisitions_${tripPublicationId}`);
      acquisitions = saved ? JSON.parse(saved) : {};
    } catch {
      acquisitions = {};
    }

    const steps = [
      ...(tripFlight?.content
        ?.flatMap((item) => item?.tripDateSummary ?? [])
        ?.flatMap((summary) => summary?.stepsDate ?? [])
        ?.flatMap((stepDate) => stepDate?.tripProgramDetails ?? []) ?? []),
    ].sort((a, b) => (a?.ranking?.rank ?? Infinity) - (b?.ranking?.rank ?? Infinity));

    const seen = new Set();
    const rows = [];

    steps.forEach((step) => {
      const stepLabel = step?.ranking?.title || step?.city || "";
      const collect = (items, type) =>
        (Array.isArray(items) ? items : []).forEach((item) => {
          const id = String(item?.id ?? item?.title ?? "");
          const counts = acquisitions?.[type]?.[id];
          const adult = Number(counts?.adult) || 0;
          const child = Number(counts?.child) || 0;
          if (!id || adult + child === 0 || seen.has(`${type}-${id}`)) return;

          seen.add(`${type}-${id}`);
          rows.push({
            id: `${type}-${id}`,
            stepLabel,
            title: item?.title ?? "",
            picture: item?.picture ?? "",
            who: [
              adult ? `${adult} ${adult > 1 ? "adultes" : "adulte"}` : "",
              child ? `${child} ${child > 1 ? "enfants" : "enfant"}` : "",
            ]
              .filter(Boolean)
              .join(" · "),
            amount:
              (Number(item?.adultPricePerPerson?.value) || 0) * adult +
              (Number(item?.childPricePerPerson?.value) || 0) * child,
            currency:
              item?.adultPricePerPerson?.currency ?? item?.childPricePerPerson?.currency ?? "",
          });
        });

      collect(step?.activities ?? step?.activites, "activity");
      collect(step?.options, "option");
    });

    return rows;
  }, [tripFlight, tripPublicationId]);

  /* Activités comprises dans le forfait, pour le carnet de voyage de la
     confirmation. Distinct de chosenExtras, qui ne liste que ce que le
     voyageur a ajouté. */
  const includedActivities = useMemo(() => {
    const titles = [];
    const seen = new Set();

    (tripFlight?.content ?? [])
      .flatMap((item) => item?.tripDateSummary ?? [])
      .flatMap((summary) => summary?.stepsDate ?? [])
      .flatMap((stepDate) => stepDate?.tripProgramDetails ?? [])
      .forEach((step) => {
        (step?.activities ?? step?.activites ?? []).forEach((activity) => {
          const title = String(activity?.title ?? "").trim();
          if (!title || seen.has(title)) return;
          seen.add(title);
          titles.push(title);
        });
      });

    return titles;
  }, [tripFlight]);

  /* Montants : prix par adulte × voyageurs, plus les suppléments. Le total
     définitif est calculé par l'API à l'étape paiement. */
  const perPerson = selectedTripDate?.adultPricePerPerson;
  const currency = perPerson?.currency ?? chosenExtras[0]?.currency ?? "";
  const extrasTotal = chosenExtras.reduce((sum, row) => sum + row.amount, 0);
  const travelersTotal = (Number(perPerson?.value) || 0) * travelers.length;

  const recapLines = [
    perPerson?.value != null && {
      label: `${travelers.length} × ${Number(perPerson.value).toLocaleString(currentLocale())} ${currency}`.trim(),
      amount: { value: travelersTotal, currency },
    },
    extrasTotal > 0 && {
      label: t("booking.recap.extras", "Options et activités"),
      amount: { value: extrasTotal, currency },
    },
  ].filter(Boolean);

  const handleContinue = () => {
    if (missingTravelers.length > 0 || invalidAgeTravelers.length > 0) {
      /* Plutôt qu'un bouton inerte : on montre ce qui bloque, sur la première
         carte concernée. */
      setShowValidation(true);
      window.requestAnimationFrame(() => {
        document
          .querySelector(`.${styles.main} [data-invalid="true"]`)
          ?.scrollIntoView({ block: "center", behavior: "smooth" });
      });
      return;
    }

    const nextQuery = new URLSearchParams({
      nbAdults: String(travelerStats.nbAdults),
      nbChilds: String(travelerStats.nbChilds),
      nbBabies: String(travelerStats.nbBabies),
    }).toString();

    const bookingPreview = {
      tripPublicationId,
      tripDateId: tripDate,
      destination: destinationLabel,
      picture: mainPicture,
      included: includedActivities,
      title: tripSummary?.title ?? "",
      selectedTripDate,
      counts: travelerStats,
      /* Montant et suppléments repris tels quels par les étapes paiement et
         confirmation : elles n'ont pas à les recalculer. */
      total: { value: travelersTotal + extrasTotal, currency },
      extras: chosenExtras.map((row) => ({
        label: row.label ?? row.title ?? "",
        amount: { value: row.amount, currency: row.currency || currency },
      })),
    };

    // ✅ Réservation confirmée → on efface les acquisitions sauvegardées
    try {
      sessionStorage.removeItem(`acquisitions_${tripPublicationId}`);
    } catch { /* silencieux */ }

    navigate(
      `/trips/${tripPublicationId}/${tripDate}/booking/payments${
        nextQuery ? `?${nextQuery}` : ""
      }`,
      { state: { travelers, bookingPreview } }
    );
  };


  if (loading) return <PortalPreloader />;

  const phone = personInfo?.phoneNumber
    ? `${personInfo.phoneNumber.indicative ?? ""} ${personInfo.phoneNumber.number ?? ""}`.trim()
    : "";

  const contact = [
    (personInfo?.firstName || personInfo?.lastName) && {
      icon: "iconUser",
      label: t("booking.contact.holder", "Titulaire"),
      value: `${personInfo?.firstName ?? ""} ${personInfo?.lastName ?? ""}`.trim(),
    },
    personInfo?.email && {
      icon: "iconEnvelope",
      label: t("booking.contact.email", "Email"),
      value: personInfo.email,
    },
    phone && { icon: "iconPhone", label: t("booking.contact.phone", "Téléphone"), value: phone },
  ].filter(Boolean);

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <Breadcrumb
          className={styles.breadcrumb}
          ariaLabel={t("trips.results.breadcrumb", "Fil d'Ariane")}
          linkComponent={Link}
          items={[
            { label: t("trips.results.home", "Accueil"), to: "/" },
            { label: t("tripDetails.header.results", "Résultats"), to: "/trips" },
            {
              label: tripSummary?.title || t("TripDetails", "Détail du voyage"),
              to: `/trips/${tripPublicationId}/${tripDate}`,
            },
            { label: t("booking.crumb", "Réservation") },
          ]}
        />

        <SectionHeader
          size="page"
          className={styles.header}
          kicker={t("booking.kicker", "Réservation")}
          title={t("booking.title", "Finaliser votre réservation")}
          subtitle={t(
            "booking.subtitle",
            "Vérifiez vos coordonnées et renseignez les voyageurs. Aucun montant n'est débité à cette étape."
          )}
        />

        <BookingSteps current={2} />

        {error && (
          <p className={styles.warning}>
            <Icon name="iconWarning" size="sm" />
            {t(
              "booking.loadError",
              "Impossible de charger tous les détails du voyage. Vous pouvez tout de même compléter les voyageurs."
            )}
          </p>
        )}

        <div className={styles.layout}>
          <div className={styles.main}>
            {contact.length > 0 && (
              <ContentCard
                title={t("booking.contact.title", "Vos coordonnées")}
                titleId="booking-contact"
                actions={
                  <Button
                    variant="ghost"
                    size="small"
                    label={t("booking.contact.edit", "Modifier dans mon profil")}
                    onClick={() => navigate("/profil")}
                  />
                }
              >
                <div className={styles.contact}>
                  {contact.map((row) => (
                    <div key={row.label}>
                      <i>
                        <Icon name={row.icon} size="sm" />
                      </i>
                      <span>
                        <small>{row.label}</small>
                        <strong title={row.value}>{row.value}</strong>
                      </span>
                    </div>
                  ))}
                </div>
                <p className={styles.note}>
                  <Icon name="iconInfo" size="sm" />
                  {t(
                    "booking.contact.note",
                    "Vos documents de voyage et la confirmation seront envoyés à cette adresse."
                  )}
                </p>
              </ContentCard>
            )}

            <ContentCard
              title={t("booking.travelers.title", "Voyageurs")}
              titleId="booking-travelers"
              actions={
                <span className={styles.count}>
                  {t("booking.travelers.progress", {
                    done: travelers.length - missingTravelers.length,
                    total: travelers.length,
                    defaultValue: `${travelers.length - missingTravelers.length} / ${travelers.length} complétés`,
                  })}
                </span>
              }
            >
              <TravelerForm
                travelers={travelers}
                onChange={handleTravelerChange}
                errors={invalidAgeTravelers}
                showErrors={showValidation}
                onReclassify={handleReclassifyTraveler}
                profile={profileTraveler}
                onUseProfile={handleUseProfile}
                ageHints={{
                  ADULT: t("booking.travelers.adultHint", {
                    min: ageLimits.adultsMinAge,
                    defaultValue: `${ageLimits.adultsMinAge} ans et plus`,
                  }),
                  CHILD: t("booking.travelers.childHint", {
                    min: ageLimits.childsMinAge,
                    max: ageLimits.childsMaxAge,
                    defaultValue: `${ageLimits.childsMinAge} à ${ageLimits.childsMaxAge} ans`,
                  }),
                  BABY: t("booking.travelers.babyHint", {
                    max: ageLimits.babiesMaxAge,
                    defaultValue: `Moins de ${ageLimits.babiesMaxAge + 1} ans`,
                  }),
                }}
              />
              <p className={styles.note}>
                <Icon name="iconShieldCheck" size="sm" />
                {t(
                  "booking.travelers.idNote",
                  "L'agence vérifie les pièces d'identité avant l'émission des documents. Rien à téléverser ici."
                )}
              </p>
            </ContentCard>

            {chosenExtras.length > 0 && (
              <ContentCard
                title={t("booking.extras.title", "Vos options")}
                titleId="booking-extras"
                actions={
                  <Button
                    variant="ghost"
                    size="small"
                    label={t("action.edit", "Modifier")}
                    onClick={() => navigate(`/trips/${tripPublicationId}/${tripDate}`)}
                  />
                }
              >
                <div className={styles.extras}>
                  {chosenExtras.map((row, index) => (
                    <React.Fragment key={row.id}>
                      {(index === 0 || chosenExtras[index - 1].stepLabel !== row.stepLabel) &&
                        row.stepLabel && <p className={styles.extraStep}>{row.stepLabel}</p>}
                      <div className={styles.extraRow}>
                        {row.picture ? (
                          <img src={row.picture} alt="" loading="lazy" />
                        ) : (
                          <span className={styles.extraNoPhoto} aria-hidden="true" />
                        )}
                        <div>
                          <strong>{row.title}</strong>
                          <small>{row.who}</small>
                        </div>
                        <b>{`${row.amount.toLocaleString(currentLocale())} ${row.currency}`.trim()}</b>
                      </div>
                    </React.Fragment>
                  ))}
                </div>
              </ContentCard>
            )}
          </div>

          <BookingRecap
            picture={mainPicture}
            kicker={t("tripDetails.header.kicker", "Voyage organisé")}
            title={tripSummary?.title || t("booking.recap.fallbackTitle", "Votre voyage")}
            startDate={selectedTripDate?.startDate}
            endDate={selectedTripDate?.endDate}
            startPlace={destinationLabel}
            durationLabel={
              selectedTripDate?.nbDays
                ? t("booking.recap.duration", {
                    days: selectedTripDate.nbDays,
                    nights: selectedTripDate.nbNight,
                    defaultValue: `${selectedTripDate.nbDays} j / ${selectedTripDate.nbNight} n`,
                  })
                : undefined
            }
            lines={recapLines}
            total={{ value: travelersTotal + extrasTotal, currency }}
            totalNote={t(
              "booking.recap.estimate",
              "Montant indicatif : le total définitif est confirmé à l'étape paiement."
            )}
            ctaLabel={t("booking.continue", "Continuer vers le paiement")}
            onContinue={handleContinue}
            secondary={
              <Button
                variant="secondary"
                size="medium"
                fullWidth
                iconName="iconArrowLeft"
                iconPosition="left"
                label={t("booking.back", "Revenir au voyage")}
                onClick={() => navigate(-1)}
              />
            }
          />
        </div>
      </div>
    </div>
  );
}

export default Preview;
